import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchAllAnnouncementGroups } from 'redux/slices/announcements/announcementGroupSlice';
import { AppDispatch, RootState } from 'redux/store';
import { AnnouncementGroupsAutocompleteProps } from 'types/autocomplete';
import Autocomplete from 'components/Common/Autocomplete';
import AutocompleteOption from 'components/Common/autocomplete/AutocompleteOption';
import { AnnouncementGroup } from 'types/announcements';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

const AnnouncementGroupAutocomplete = ({ defaultValue, setGroup, ...props }: AnnouncementGroupsAutocompleteProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const parishId = useSelector(selectSelectedParishId);
    // This picker has no pagination UI of its own — it needs every group in one shot, so it
    // reads the separate pickerRecords/pickerLoading state (fetchAllAnnouncementGroups) rather
    // than the table's page-scoped records (fetchAnnouncementGroups), which only ever holds
    // whatever page the Announcement Groups table last requested.
    const { loading = false, records = [] } = useSelector((state: RootState) => ({
        loading: state.announcement.announcementGroups.pickerLoading,
        records: state.announcement.announcementGroups.pickerRecords,
    }));
    let resolvedDefault = defaultValue;
    if (defaultValue && typeof defaultValue !== 'object') {
        const group = records.find((g) => String(g.id) === String(defaultValue));
        if (group) {
            resolvedDefault = group;
        }
    }

    useEffect(() => {
        if (!parishId) return;
        if (!defaultValue) {
            dispatch(fetchAllAnnouncementGroups({ parishId }));
        }
    }, [dispatch, defaultValue, parishId]);

    return (
        <Autocomplete
            loading={loading}
            label="To"
            placeholder="Announcement Groups"
            options={records}
            disabled={!!defaultValue}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches Autocomplete's untyped MUI event param
            onChange={(_: any, value: AnnouncementGroup | null) => setGroup(value)}
            defaultValue={resolvedDefault}
            getOptionLabel={(option) => option?.title}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches Autocomplete's untyped MUI renderOption params
            renderOption={(props: any, option: any) => {
                const { title: name, contact: email, imgUrl } = option;
                const _option = { name, email, imgUrl };
                return <AutocompleteOption option={_option} props={props} />;
            }}
            getOptionKey={(option) => (typeof option === 'object' ? option?.id : option)}
            {...props}
        />
    );
};

export default AnnouncementGroupAutocomplete;
